
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./AppHome.css";
import "../Admin/Admin.css";

const BASE_URL = "https://gym-invoice-back.onrender.com";

// =========================================================
// TABLE STYLES
// =========================================================

const thStyle = {
  border: "1px solid #000",
  padding: "10px",
  textAlign: "center",
  fontWeight: "bold",
  backgroundColor: "#ffffff",
};

const tdStyle = {
  border: "1px solid #000",
  padding: "10px",
  backgroundColor: "#ffffff",
};

const tdStyleRight = {
  border: "1px solid #000",
  padding: "10px",
  textAlign: "right",
  backgroundColor: "#ffffff",
};


// =========================================================
// FORMAT AMOUNT
// =========================================================

const formatAmount = (value = 0) =>
  Number(value || 0).toLocaleString("en-LK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });


// =========================================================
// COMPONENT
// =========================================================

const NetIncomeCalculator = () => {

  const navigate = useNavigate();

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Branch filter
  const [branchCode, setBranchCode] = useState("");

  const [totalPayment, setTotalPayment] = useState(0);
  const [totalRegFee, setTotalRegFee] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [netProfit, setNetProfit] = useState(0);

  const [calculated, setCalculated] = useState(false);
  const [loading, setLoading] = useState(false);


  // =========================================================
  // LOGIN CHECK
  // =========================================================

  useEffect(() => {

    const role = sessionStorage.getItem("userRole");

    if (!role) {
      navigate("/");
    }

  }, [navigate]);


  // =========================================================
  // CALCULATE NET INCOME
  // =========================================================

  const calculateNetIncome = async () => {

    if (!fromDate || !toDate) {

      alert("Please select both From and To dates.");

      return;
    }


    if (fromDate > toDate) {

      alert("From Date cannot be greater than To Date.");

      return;
    }


    setLoading(true);

    try {

      // =====================================================
      // CREATE QUERY PARAMETERS
      // =====================================================

      const params = new URLSearchParams();

      params.append("from", fromDate);
      params.append("to", toDate);

      // Only send branchCode when a specific branch is selected
      if (branchCode) {
        params.append("branchCode", branchCode);
      }


      // =====================================================
      // GET PAYMENTS
      // =====================================================

      const paymentsRes = await axios.get(
        `${BASE_URL}/api/payments/range?${params.toString()}`
      );


      // =====================================================
      // GET EXPENSES
      // =====================================================

      const expensesRes = await axios.get(
        `${BASE_URL}/api/expenditures/range?${params.toString()}`
      );


      // =====================================================
      // GET MEMBERS / REGISTRATION FEES
      // =====================================================

      const membersRes = await axios.get(
        `${BASE_URL}/api/members/range?${params.toString()}`
      );


const paymentSum = (paymentsRes.data || []).reduce(
  (sum, payment) => sum + Number(payment.amount || 0),
  0
);

const regFeeSum = (membersRes.data || []).reduce(
  (sum, member) => sum + Number(member.regFee || 0),
  0
);

const expensesSum = (expensesRes.data || []).reduce(
  (sum, expenditure) => sum + Number(expenditure.cost || 0),
  0
);

const calculatedNetProfit =
  paymentSum + regFeeSum - expensesSum;

setTotalPayment(paymentSum);
setTotalRegFee(regFeeSum);
setTotalExpenses(expensesSum);
setNetProfit(calculatedNetProfit);


    } catch (error) {

      console.error(
        "Error calculating net income:",
        error
      );

      alert(
        "Error fetching financial data. Please try again."
      );

    } finally {

      setLoading(false);

    }
  };


  // =========================================================
  // DOWNLOAD PDF
  // =========================================================

  const downloadPdf = async () => {

    if (!fromDate || !toDate) {

      alert("Please select both dates first!");

      return;
    }


    if (fromDate > toDate) {

      alert("From Date cannot be greater than To Date.");

      return;
    }


    try {

      // =====================================================
      // CREATE PDF PARAMETERS
      // =====================================================

      const params = new URLSearchParams();

      params.append("from", fromDate);
      params.append("to", toDate);

      if (branchCode) {
        params.append("branchCode", branchCode);
      }


      // =====================================================
      // REQUEST PDF
      // =====================================================

      const response = await axios({

        url:
          `${BASE_URL}/api/finance-report/pdf?${params.toString()}`,

        method: "GET",

        responseType: "blob",

      });


      // =====================================================
      // CREATE DOWNLOAD
      // =====================================================

      const url = window.URL.createObjectURL(
        new Blob([response.data], {
          type: "application/pdf",
        })
      );


      const link = document.createElement("a");

      link.href = url;


      const branchName =
        branchCode || "ALL";

      link.setAttribute(
        "download",
        `finance_report_${branchName}_${fromDate}_${toDate}.pdf`
      );


      document.body.appendChild(link);

      link.click();

      link.remove();


      window.URL.revokeObjectURL(url);


    } catch (error) {

      console.error(
        "Error downloading finance report:",
        error
      );

      alert(
        "Error downloading finance report."
      );

    }
  };


  // =========================================================
  // CLEAR / RESET
  // =========================================================

  const clearResults = () => {

    setFromDate("");

    setToDate("");

    setBranchCode("");

    setTotalPayment(0);

    setTotalRegFee(0);

    setTotalExpenses(0);

    setNetProfit(0);

    setCalculated(false);

  };


  // =========================================================
  // DISPLAY BRANCH NAME
  // =========================================================

  const selectedBranch =
    branchCode || "ALL BRANCHES";


  // =========================================================
  // RETURN
  // =========================================================

  return (

    <div
      style={{
        padding: "20px",
        maxWidth: "700px",
        margin: "auto",
      }}
    >

      {/* =================================================== */}
      {/* TITLE */}
      {/* =================================================== */}

      <h2>
        Net Income Calculator
      </h2>


      {/* =================================================== */}
      {/* FILTER SECTION */}
      {/* =================================================== */}

      <div
        style={{
          marginBottom: "15px",
        }}
      >

        {/* FROM DATE */}

        <div
          style={{
            marginBottom: "10px",
          }}
        >

          <label>

            From:{" "}

            <input
              type="date"
              value={fromDate}
              onChange={(e) =>
                setFromDate(e.target.value)
              }
            />

          </label>

        </div>


        {/* TO DATE */}

        <div
          style={{
            marginBottom: "10px",
          }}
        >

          <label>

            To:{" "}

            <input
              type="date"
              value={toDate}
              onChange={(e) =>
                setToDate(e.target.value)
              }
            />

          </label>

        </div>


        {/* ================================================= */}
        {/* BRANCH CODE */}
        {/* ================================================= */}

        <div
          style={{
            marginBottom: "10px",
          }}
        >

          <label>

            Branch Code:{" "}

            <select
              value={branchCode}
              onChange={(e) =>
                setBranchCode(e.target.value)
              }
            >

              <option value="">
                ALL BRANCHES
              </option>

              <option value="KB">
                KB
              </option>

              <option value="WB">
                WB
              </option>

            </select>

          </label>

        </div>

      </div>


      {/* =================================================== */}
      {/* BUTTONS */}
      {/* =================================================== */}

      <div
        style={{
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
        }}
      >

        <button
          onClick={calculateNetIncome}
          disabled={loading}
        >

          {loading
            ? "Calculating..."
            : "Calculate Net Income"}

        </button>


        <button
          onClick={downloadPdf}
          disabled={loading}
        >
          Download PDF
        </button>


        <button
          onClick={clearResults}
        >
          Clear
        </button>

      </div>


      {/* =================================================== */}
      {/* FINANCE SUMMARY */}
      {/* =================================================== */}

      {calculated && fromDate && toDate && (

        <div
          style={{
            marginTop: "30px",
          }}
        >

          <h3
            style={{
              textAlign: "center",
              color: "#ffffff",
              fontWeight: "bold",
            }}
          >
            Finance Summary
          </h3>


          {/* PERIOD */}

          <p
            style={{
              textAlign: "center",
              marginBottom: "5px",
              color: "#ffffff",
              fontWeight: "bold",
            }}
          >

            Period: {fromDate} to {toDate}

          </p>


          {/* BRANCH */}

          <p
            style={{
              textAlign: "center",
              marginBottom: "10px",
              color: "#ffffff",
              fontWeight: "bold",
            }}
          >

            Branch: {selectedBranch}

          </p>


          {/* ================================================= */}
          {/* SUMMARY TABLE */}
          {/* ================================================= */}

          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
            }}
          >

            <thead>

              <tr
                style={{
                  backgroundColor: "#ffffff",
                }}
              >

                <th style={thStyle}>
                  Description
                </th>

                <th style={thStyle}>
                  Amount (LKR)
                </th>

              </tr>

            </thead>


            <tbody>

              {/* PAYMENT */}

              <tr>

                <td style={tdStyle}>
                  Total Payment Received
                </td>

                <td style={tdStyleRight}>

                  {formatAmount(
                    totalPayment
                  )}

                </td>

              </tr>


              {/* REGISTRATION FEE */}

              <tr>

                <td style={tdStyle}>
                  Total Registration Fees
                </td>

                <td style={tdStyleRight}>

                  {formatAmount(
                    totalRegFee
                  )}

                </td>

              </tr>


              {/* EXPENSES */}

              <tr>

                <td style={tdStyle}>
                  Total Expenses
                </td>

                <td style={tdStyleRight}>

                  (
                  {formatAmount(
                    totalExpenses
                  )}
                  )

                </td>

              </tr>


              {/* NET PROFIT */}

              <tr>

                <td
                  style={{
                    ...tdStyle,
                    fontWeight: "bold",
                  }}
                >

                  Net Profit

                </td>

                <td
                  style={{
                    ...tdStyleRight,
                    fontWeight: "bold",
                    color:
                      netProfit >= 0
                        ? "green"
                        : "red",
                  }}
                >

                  {formatAmount(
                    netProfit
                  )}

                </td>

              </tr>

            </tbody>

          </table>

        </div>

      )}

    </div>

  );
};

export default NetIncomeCalculator;

